import axios from 'axios';
import { API_BASE_URL } from '../config/apiBaseUrl';

const api = axios.create({
  baseURL: API_BASE_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Log para debug de requisições de membros
  if (config.url?.includes('/membros') && (config.method === 'post' || config.method === 'put')) {
    console.log('🌐 AXIOS REQUEST - URL:', config.url);
    console.log('🌐 AXIOS REQUEST - Method:', config.method);
    console.log('🌐 AXIOS REQUEST - Headers:', config.headers);
    
    if (config.data instanceof FormData) {
      console.log('🌐 AXIOS REQUEST - Data: FormData');
      // Log campos específicos do FormData
      if (config.data.has('filho_membro')) {
        console.log('🌐 AXIOS REQUEST - filho_membro:', config.data.get('filho_membro'));
      }
      if (config.data.has('filhos')) {
        console.log('🌐 AXIOS REQUEST - filhos:', config.data.get('filhos'));
      }
      
      // Log completo do FormData
      console.log('🌐 AXIOS REQUEST - FormData completo:');
      for (let pair of config.data.entries()) {
        if (pair[0].includes('filho') || pair[0] === 'filho_membro' || pair[0] === 'filhos') {
          console.log(`  - ${pair[0]}:`, pair[1]);
        }
      }
    } else {
      console.log('🌐 AXIOS REQUEST - Data type:', typeof config.data);
      console.log('🌐 AXIOS REQUEST - Data completo:', JSON.stringify(config.data, null, 2));
      console.log('🌐 AXIOS REQUEST - filho_membro:', config.data?.filho_membro);
      console.log('🌐 AXIOS REQUEST - filhos:', JSON.stringify(config.data?.filhos, null, 2));
      console.log('🌐 AXIOS REQUEST - qtd_filhos:', config.data?.qtd_filhos);
    }
  }
  
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
};

// Igrejas
export const igrejasApi = {
  list: () => api.get('/igrejas'),
  get: (id) => api.get(`/igrejas/${id}`),
  create: (data) => api.post('/igrejas', data),
  update: (id, data) => api.put(`/igrejas/${id}`, data),
};

// Membros
export const membrosApi = {
  list: (params) => api.get('/membros', { params }),
  get: (id) => api.get(`/membros/${id}`),
  create: (data, config) => {
    // Se data é FormData, o axios detecta automaticamente e define Content-Type com boundary
    if (data instanceof FormData) {
      return api.post('/membros', data, {
        ...config,
        headers: {
          ...config?.headers,
          // Não definir Content-Type manualmente - axios fará isso com o boundary correto
        },
      });
    }
    return api.post('/membros', data, config);
  },
  update: (id, data, config) => {
    // Se data é FormData, o axios detecta automaticamente e define Content-Type com boundary
    if (data instanceof FormData) {
      return api.put(`/membros/${id}`, data, {
        ...config,
        headers: {
          ...config?.headers,
          // Não definir Content-Type manualmente - axios fará isso com o boundary correto
        },
      });
    }
    return api.put(`/membros/${id}`, data, config);
  },
  delete: (id) => api.delete(`/membros/${id}`),
  aniversariantesHoje: (igrejaId) => api.get('/membros/aniversariantes/hoje', { params: { igreja_id: igrejaId } }),
  aniversariantesMes: (mes, igrejaId) => api.get('/membros/aniversariantes/mes', { params: { mes, igreja_id: igrejaId } }),
  getPdf: (id) => api.get(`/membros/${id}/ficha-pdf`, { responseType: 'blob' }),
  vincularConjuge: (membroId, conjugeId) => api.post('/membros/vincular-conjuge', { membro_id: membroId, conjuge_id: conjugeId }),
};

// Financeiro
export const financeiroApi = {
  listLancamentos: (params) => api.get('/financeiro/lancamentos', { params }),
  getLancamentosPorIgreja: (params) => api.get('/financeiro/lancamentos-por-igreja', { params }),
  createLancamento: (data) => api.post('/financeiro/lancamentos', data),
  deleteLancamento: (id) => api.delete(`/financeiro/lancamentos/${id}`),
  getResumo: (params) => api.get('/financeiro/resumo', { params }),
  getGraficoMensal: (params) => api.get('/financeiro/grafico-mensal', { params }),
};

// EBD
export const ebdApi = {
  listSalas: (igrejaId) => api.get('/ebd/salas', { params: { igreja_id: igrejaId } }),
  createSala: (data) => {
    const form = new FormData();
    Object.entries(data).forEach(([k, v]) => { if (v != null) form.append(k, v); });
    return api.post('/ebd/salas', form);
  },
  updateSala: (salaId, data) => {
    const form = new FormData();
    Object.entries(data).forEach(([k, v]) => { if (v != null) form.append(k, v); });
    return api.put(`/ebd/salas/${salaId}`, form);
  },
  listAlunos: (params) => api.get('/ebd/alunos', { params }),
  createAluno: (data) => api.post('/ebd/alunos', data),
  updateAluno: (alunoId, data) => api.put(`/ebd/alunos/${alunoId}`, data),
  listPresencas: (params) => api.get('/ebd/presencas', { params }),
  createPresenca: (data) => api.post('/ebd/presencas', data),
  getRanking: (params) => api.get('/ebd/ranking', { params }),
  getSalaOfertaDia: (params) => api.get('/ebd/sala-oferta-dia', { params }),
  putSalaOfertaDia: (data) => api.put('/ebd/sala-oferta-dia', data),
  getOfertasRelatorio: (params) => api.get('/ebd/ofertas-relatorio', { params }),
  deleteSala: (salaId) => api.delete(`/ebd/salas/${salaId}`),
  deleteAluno: (alunoId) => api.delete(`/ebd/alunos/${alunoId}`),
};

// Dashboard
export const dashboardApi = {
  getStats: (igrejaId) => api.get('/dashboard/stats', { params: { igreja_id: igrejaId } }),
};

// Grupos Familiares
export const gruposFamiliaresApi = {
  get: (id) => api.get(`/grupos-familiares/${id}`),
};

// Cargos
export const cargosApi = {
  list: (params) => api.get('/cargos', { params }),
  get: (id) => api.get(`/cargos/${id}`),
  create: (data) => api.post('/cargos', data),
  update: (id, data) => api.put(`/cargos/${id}`, data),
  delete: (id) => api.delete(`/cargos/${id}`),
};

// Utils
export const utilsApi = {
  buscarCep: (cep) => api.get(`/utils/cep/${cep}`),
};

export default api;
